import { Link } from '@remix-run/react';
import { useEffect, useState } from 'react';

import type { CalendarEarning } from '~/types/earningsCalendar.types';

import { tickerLogoUrl } from '../utils/tickerLogoUrl';

const TILE_CLASS =
  'group flex h-full min-h-0 w-full min-w-0 items-center rounded-lg border border-slate-900/[0.06] bg-background px-1.5 py-1.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.03)] transition-colors hover:border-emerald-200/80 hover:bg-emerald-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-emerald-600 md:px-2 md:py-2';

function CompanyLogo({ item }: { item: CalendarEarning }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initials = item.symbol.slice(0, 2).toUpperCase();
  const logoSrc = tickerLogoUrl(item);

  useEffect(() => {
    setLogoFailed(false);
  }, [item.symbol, logoSrc]);

  return (
    <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-slate-700 md:size-10 md:rounded-lg">
      {!logoFailed ? (
        <img
          key={`${item.symbol}-${logoSrc}`}
          src={logoSrc}
          alt=""
          className="size-5 object-contain md:size-6"
          loading="lazy"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        <span className="flex size-full items-center justify-center text-[9px] font-bold text-slate-300 md:text-[10px]">
          {initials}
        </span>
      )}
    </span>
  );
}

function CompanyTileContent({ item }: { item: CalendarEarning }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5 md:gap-2">
      <CompanyLogo item={item} />
      <span className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
        <span className="min-w-0 truncate text-xs font-semibold leading-none tabular-nums tracking-tight text-[#1c1c1c] md:text-xs">
          {item.symbol}
        </span>
        <span className="min-w-0 truncate text-[10px] font-semibold leading-none tabular-nums tracking-tight text-slate-500 md:text-[11px]">
          {item.formattedMarketCap ?? '—'}
        </span>
      </span>
    </span>
  );
}

export default function CompanyTile({ item }: { item: CalendarEarning }) {
  const title = `${item.name} (${item.symbol})`;

  if (!item.tickerHref) {
    return (
      <span className={TILE_CLASS} title={title}>
        <CompanyTileContent item={item} />
      </span>
    );
  }

  return (
    <Link to={item.tickerHref} className={TILE_CLASS} title={title}>
      <CompanyTileContent item={item} />
    </Link>
  );
}
